import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Trophy, X, Users, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AgentLeaderboard = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API}/deep-research/leaderboard?limit=30`)
      .then((r) => setData(r.data))
      .catch(() => setData({ top_agents: [], unique_agents_used: 0 }))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="agent-leaderboard"
        className="max-w-2xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-zinc-900 via-zinc-900 to-purple-950/30 border-purple-500/30 p-0"
      >
        <button
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-zinc-800/60 hover:bg-zinc-700 text-zinc-300"
        >
          <X size={16} />
        </button>

        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-pink-500 flex items-center justify-center">
              <Trophy size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">{t("dr_leaderboard_title")}</h2>
              <p className="text-xs text-zinc-400">{t("dr_leaderboard_desc")}</p>
            </div>
          </div>
          {data && (
            <div className="mt-3 flex items-center gap-2 text-xs text-purple-300">
              <Users size={12} />
              <span>{data.unique_agents_used} {t("dr_leaderboard_unique")}</span>
            </div>
          )}
        </div>

        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-purple-400" />
            </div>
          )}
          {!loading && data?.top_agents?.length === 0 && (
            <p className="text-center text-zinc-500 py-12 text-sm">Henüz veri yok.</p>
          )}
          {!loading && data?.top_agents?.length > 0 && (
            <div className="space-y-2">
              {data.top_agents.map((a, i) => (
                <LeaderRow key={a.agent_id} row={a} rank={i + 1} maxCalls={data.top_agents[0].call_count} t={t} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const rankStyle = (rank) => {
  if (rank === 1) return "bg-gradient-to-br from-amber-400 to-yellow-600 text-zinc-900";
  if (rank === 2) return "bg-gradient-to-br from-zinc-300 to-zinc-500 text-zinc-900";
  if (rank === 3) return "bg-gradient-to-br from-amber-700 to-amber-900 text-white";
  return "bg-zinc-800 text-zinc-400";
};

const LeaderRow = ({ row, rank, maxCalls, t }) => {
  const barPct = maxCalls ? (row.call_count / maxCalls) * 100 : 0;
  return (
    <div
      data-testid={`leaderboard-row-${rank}`}
      className="flex items-center gap-3 p-3 rounded-lg border border-zinc-800 bg-zinc-800/30 hover:bg-zinc-800/60 transition-colors"
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${rankStyle(rank)}`}>
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white truncate">{row.name}</span>
          <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{row.role}</span>
        </div>
        <div className="mt-1 h-1.5 bg-zinc-900 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            style={{ width: `${barPct}%` }}
          />
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm font-bold text-purple-300">{row.call_count}</div>
        <div className="text-[10px] text-zinc-500">{t("dr_leaderboard_calls")}</div>
      </div>
    </div>
  );
};

export default AgentLeaderboard;
